<template>
  <div class="wrapper">
    <h1 class="page-title">
      人員管理
      <small class="subtitle">此處可以查看/調整人員資訊。</small>
    </h1>

    <div class="member-table">
      <div class="member-table__header">
        <h2 class="member-table__header--title">人員名單</h2>
        <b-dropdown v-if="isRoleGreaterThanHead" position="is-bottom-left">
          <template #trigger="{ active }">
            <b-button type="is-primary" class="member-table__header--action">
              新增人員
              <i :class="{'icon-up-chevron': active, 'icon-down-chevron': !active}" />
            </b-button>
          </template>
          <b-dropdown-item @click="onCreateMember">新增人員</b-dropdown-item>
          <b-dropdown-item @click="onBatchCreateMember">批次新增/更新人員</b-dropdown-item>
        </b-dropdown>
        <p v-else class="member-table__header--subtitle">此為唯讀模式，如需新增/編輯人員資訊，請洽管理員或帳號擁有者。</p>
        <div class="mb-4">
          <div class="search-block">
            <div class="flex flex-shrink items-center justify-between min-w-1/2 w-1/2">
              <w-select
                v-model="searchBy"
                :options="searchByOptions"
                class="w-32"
              />
              <b-field class="flex-grow ml-4">
                <b-input
                  :placeholder="searchPlaceholder"
                  v-model="search"
                  @keypress.enter.native="onSearch"
                />
              </b-field>
            </div>
            <div class="flex flex-wrap gap-4 justify-between ml-4 w-1/2">
              <div class="flex flex-shrink-0 gap-6">
                <w-button
                  :disabled="disableSearchButton"
                  size="sm"
                  @click="onSearch"
                >
                  搜尋
                </w-button>
                <w-button
                  :disabled="isGettingMembers"
                  color-type="secondary"
                  size="sm"
                  type="link"
                  @click="onClearSearchFilters"
                >
                  清除
                </w-button>
                <w-button
                  size="sm"
                  type="link"
                  @click="showAdvancedSearchBlock = !showAdvancedSearchBlock"
                >
                  進階搜尋
                  <i
                    :class="showAdvancedSearchBlock ? 'icon-up-chevron' : 'icon-down-chevron'"
                    class="ml-2"
                  />
                </w-button>
              </div>
              <w-button
                :disabled="isPreparingDownload"
                class="flex-shrink-0"
                color-type="secondary"
                size="func"
                type="link"
                @click="onDownloadMembers"
              >
                <span class="flex items-center">
                  <i class="icon-download mr-2 text-xs text-secondary-500" />
                  匯出人員名單
                  <i v-if="isPreparingDownload" class="h-3 loading ml-2 w-3" />
                </span>
              </w-button>
            </div>
          </div>
          <div
            v-if="showAdvancedSearchBlock"
            class="bg-light-gray-400 mt-2 px-4 py-2 rounded"
          >
            <div class="flex flex-wrap gap-y-4 item-center">
              <div class="flex items-center">
                <label class="flex-shrink-0 mr-4 text-sm">角色</label>
                <button
                  v-for="roles in userRoles"
                  :key="`user-role-${roles.key}`"
                  :class="{
                    active: advancedSearchRoles?.includes(roles.key)
                  }"
                  class="flex-shrink-0 rounded-button ml-2"
                  @click="onSelectSearchTag(advancedSearchRoles, roles.key)"
                >
                  {{ roles.name }}
                </button>
              </div>
              <div class="border-light-gray-500 border-r mx-6" />
              <div class="flex flex-grow items-baseline">
                <label class="flex-shrink-0 mr-4 text-sm">部門</label>
                <w-select
                  v-model="advancedSearchUnits"
                  :options="userUnits"
                  class="flex-grow max-w-[400px]"
                  multiple
                  placeholder="請選擇部門"
                  show-checked-icon
                />
              </div>
            </div>
            <div class="flex items-center mt-4">
              <label class="flex-shrink-0 mr-4 text-sm">建立日期</label>
              <b-datepicker
                :date-formatter="dateFormatter"
                :first-day-of-week="1"
                :max-date="maxDate"
                class="w-[312px]"
                icon="calendar-alt"
                locale="zh-TW"
                placeholder="請選擇日期範圍"
                range
                v-model="advancedSearchDates"
              />
            </div>
          </div>
        </div>
        <div
          :class="isTableCheckable ? 'justify-between' : 'justify-end'"
          class="member-table__header--toggle-column"
        >
          <div class="flex items-center">
            <w-button
              v-if="isTableCheckable"
              :disabled="!checkedRows.length"
              class="batch-button"
              color-type="secondary"
              size="func"
              type="text"
              @click="onShowPrintPreview"
            >
              <i class="icon-printer mr-2 text-base" />
              <span class="text-xs">批次列印專屬條碼</span>
            </w-button>
            <w-button
              v-if="isTableCheckable && isRoleGreaterThanHead"
              :disabled="!checkedRows.length"
              class="ml-6 batch-button"
              color-type="secondary"
              size="func"
              type="text"
              @click="openBatchDeleteDialog = true"
            >
              <i class="icon-trash mr-2 text-base" />
              <span class="text-xs">批次刪除</span>
            </w-button>
          </div>
          <b-dropdown
            :close-on-click="false"
            aria-role="list"
            position="is-bottom-left"
            append-to-body
          >
            <template #trigger>
              <w-button size="func" color-type="secondary" type="link">
                <i class="icon-sort" />
                欄位調整
              </w-button>
            </template>

            <b-dropdown-item
              aria-role="listitem"
              v-for="(column, index) in columns"
              :key="column.column">
              <b-field>
                <b-checkbox v-model="columns[index].visible">
                  {{ column.name }}
                </b-checkbox>
              </b-field>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-table
        :checkable="isTableCheckable"
        :checked-rows.sync="checkedRows"
        :class="{ 'member-table--editable': isRoleGreaterThanHead }"
        :data="members"
        :loading="loading || isGettingMembers"
        :mobile-cards="false"
        :row-class="memberRowClass"
        hoverable
        scrollable
        sticky-header
        @click="editMember"
      >
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.index"
          field="id"
          label="No."
          sticky
          v-slot="props"
          width="60px"
        >
          {{ props.index + 1 + (page - 1) * perPage }}.
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="created_at"
          label="建立日期"
          width="140px"
          :visible="columnVisibility.created_at"
          sortable
          sticky
          v-slot="props"
        >
          {{ formatTime(props.row.created_at, 'YYYY/MM/DD') }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          field="member_detail.serial"
          label="人員編號"
          width="100px"
          :visible="columnVisibility.id"
          sortable
          sticky
          v-slot="props"
        >
          {{ props.row.member_detail ? props.row.member_detail.serial : '' }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.account"
          field="account"
          label="帳號"
          sortable
          sticky
          v-slot="props"
          width="150px"
        >
          {{ props.row.account }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.role"
          field="role"
          label="角色"
          sortable
          sticky
          v-slot="props"
          width="102px"
        >
          <span
            v-if="props.row.role != null && props.row.role !== roleMapping.NORMAL"
            class="role is-inline-block"
            :class="roleBadge[props.row.role]?.style">
            {{ roleBadge[props.row.role]?.name || '' }}
          </span>
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.name"
          field="member_detail.name"
          label="姓名"
          sortable
          sticky
          v-slot="props"
          width="120px"
        >
          {{ props.row.member_detail ? props.row.member_detail.name : '' }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.department"
          field="department_id"
          label="部門"
          sortable
          sticky
          v-slot="props"
          width="76px"
        >
          {{ getDepartmentName(props.row.department_id) }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.email"
          field="email"
          label="Email"
          sortable
          v-slot="props"
        >
          {{ props.row.member_detail ? props.row.member_detail.email : '' }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.extension"
          field="member_detail.ext"
          label="分機/電話"
          sortable
          v-slot="props"
          width="106px"
        >
          {{ props.row.member_detail ? props.row.member_detail.ext : '' }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.mobile"
          field="member_detail.phone"
          label="手機號碼"
          sortable
          v-slot="props"
          width="121px"
        >
          {{ props.row.member_detail ? mapPhoneNumber(props.row.member_detail.phone) : '' }}
        </b-table-column>
        <b-table-column
          :th-attrs="columnThAttrs"
          :visible="columnVisibility.note"
          cell-class="note-cell"
          field="member_detail.note"
          label="備註"
          v-slot="props"
          width="100px"
        >
          <span :title="props.row.member_detail.note">
            {{ props.row.member_detail ? props.row.member_detail.note : '' }}
          </span>
        </b-table-column>

        <template #empty>
          <div v-if="!loading" class="my-5 has-text-centered">
            尚未建立名單喔，立刻
            <router-link to="/member/create">新增人員</router-link>
            唷！
          </div>
        </template>
      </b-table>
      <div
        v-if="total && !loading && !isGettingMembers"
        class="flex gap-4 items-center"
      >
        <b-select v-model="perPage">
          <option
            v-for="option in perPageOptions"
            :value="option.value"
            :key="option.label"
          >
            {{ option.label }}
          </option>
        </b-select>
        <div class="flex-grow">
          <b-pagination
            :total="total"
            v-model="page"
            range-before="3"
            range-after="1"
            nextIcon="right-chevron"
            prevIcon="left-chevron"
            :per-page="perPage"
          />
        </div>
      </div>
    </div>

    <print-qr-code
      v-if="showPreviewPrintQRCodes"
      v-model="showPreviewPrintQRCodes"
      :list="checkedRows"
      @close="showPreviewPrintQRCodes = false"
    />
    <w-modal v-model="openBatchDeleteDialog" type="danger">
      <template #header>
        <h1>{{ hasGreaterThanHead ? '無法' : '確認' }}批次刪除人員</h1>
      </template>
      <template #content>
        <div class="flex">
          <i class="icon-alert-circle mr-4">
            <span class="path1" />
            <span class="path2" />
            <span class="path3" />
          </i>
          <p>{{ batchDeleteContent }}</p>
        </div>
      </template>
      <template #footer>
        <w-button
          :disabled="deletingMembers"
          :loading="deletingMembers"
          color-type="error"
          @click="hasGreaterThanHead ? openBatchDeleteDialog = false : onBatchDeleteUser()"
        >
          {{ hasGreaterThanHead ? '確認' : '批次刪除' }}
        </w-button>
      </template>
    </w-modal>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';

import { batchDeleteMembers, downloadMemberList } from '@/api/member';
import PrintQrCode from '@/components/Member/PrintQrCode.vue';
import { GET_MEMBER_LIST } from '@/store/member/constants';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  MEMBER_STATUS,
  NO_DEPARTMENT_ID,
  PER_PAGE_OPTIONS,
  SNACKBAR_TYPES,
  USER_LEVEL_MAPPING,
  USER_LEVEL,
} from '@/utils/constants';
import {
  csvDownloader,
  mapPhoneNumber,
} from '@/utils/helper';

const CHECKBOX_KEY = '_checkbox';
const SEARCH_OPTION_KEYS = {
  ALL: 'all',
  ACCOUNT: 'account',
  EMAIL: 'email',
  NAME: 'name',
  SERIAL: 'serial',
};
const SEARCH_OPTION_VALUES = {
  ALL: '全部',
  ACCOUNT: '帳號',
  EMAIL: 'Email',
  NAME: '姓名',
  SERIAL: '人員編號',
};
const searchOptions = [
  {
    key: SEARCH_OPTION_KEYS.NAME,
    value: SEARCH_OPTION_VALUES.NAME,
  },
  {
    key: SEARCH_OPTION_KEYS.ACCOUNT,
    value: SEARCH_OPTION_VALUES.ACCOUNT,
  },
  {
    key: SEARCH_OPTION_KEYS.EMAIL,
    value: SEARCH_OPTION_VALUES.EMAIL,
  },
  {
    key: SEARCH_OPTION_KEYS.SERIAL,
    value: SEARCH_OPTION_VALUES.SERIAL,
  },
];

export default {
  name: 'MemberList',
  components: {
    PrintQrCode,
  },
  data() {
    return {
      advancedSearchDates: [],
      advancedSearchRoles: [],
      advancedSearchUnits: [],
      checkedRows: [],
      columns: [
        {
          column: CHECKBOX_KEY,
          name: '勾選框',
          visible: true,
        },
        {
          column: 'index',
          name: 'No.',
          visible: true,
        },
        {
          column: 'created_at',
          name: '建立日期',
          visible: false,
        },
        {
          column: 'id',
          name: '人員編號',
          visible: true,
        },
        {
          column: 'account',
          name: '帳號',
          visible: true,
        },
        {
          column: 'role',
          name: '角色',
          visible: true,
        },
        {
          column: 'name',
          name: '姓名',
          visible: true,
        },
        {
          column: 'department',
          name: '部門',
          visible: true,
        },
        {
          column: 'email',
          name: 'Email',
          visible: false,
        },
        {
          column: 'extension',
          name: '分機/電話',
          visible: false,
        },
        {
          column: 'mobile',
          name: '手機號碼',
          visible: false,
        },
        {
          column: 'note',
          name: '備註',
          visible: false,
        },
      ],
      deletingMembers: false,
      departments: [],
      isPreparingDownload: false,
      loading: false,
      members: [],
      openBatchDeleteDialog: false,
      page: FIRST_PAGE,
      perPage: DEFAULT_PER_PAGE,
      roleMapping: USER_LEVEL,
      search: '',
      searchBy: {
        key: SEARCH_OPTION_KEYS.ALL,
        value: SEARCH_OPTION_VALUES.ALL,
      },
      searchByOptions: [
        {
          key: SEARCH_OPTION_KEYS.ALL,
          value: SEARCH_OPTION_VALUES.ALL,
        },
        ...searchOptions,
      ],
      showAdvancedSearchBlock: false,
      showPreviewPrintQRCodes: false,
      statusMapping: MEMBER_STATUS,
    };
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async mounted() {
    try {
      await Promise.all([
        this.getMemberList({ page: FIRST_PAGE, per_page: this.perPage }),
        this.loadDepartmentList(),
      ]);
    } catch {
      Toast.open({
        message: '載入人員清單失敗，請稍後再試',
        type: 'is-danger',
      });
    } finally {
      this.loading = false;
    }
  },
  watch: {
    allMembers: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.members = cloneDeep(newVal);
        }
      },
    },
    page(newVal) {
      this.getMemberList({ page: newVal, per_page: this.perPage, ...this.searchQuery });
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
  },
  computed: {
    ...mapGetters('user', ['currentRole']),
    ...mapState('member', {
      isGettingMembers: ({ isGettingMembers }) => isGettingMembers,
      membersList: ({ members }) => members,
    }),
    allMembers() {
      return this.membersList?.data || [];
    },
    batchDeleteContent() {
      return this.hasGreaterThanHead ? '團主及管理員的帳號無法被批次刪除。請調整人員的角色權限後重試一次。' : '請注意，帳號經刪除後將無法繼續登入使用，尚未領取的訂單亦將自動取消。請確認是否刪除？';
    },
    columnVisibility() {
      const mapping = {};

      this.columns.forEach((c) => {
        mapping[c.column] = c.visible;
      });
      return mapping;
    },
    disableSearchButton() {
      return this.isGettingMembers
        || (!this.advancedSearchRoles?.length
        && !this.advancedSearchUnits?.length
        && !this.search
        && !this.advancedSearchDates?.length);
    },
    hasGreaterThanHead() {
      return this.checkedRows?.some(({ role }) => role >= USER_LEVEL.HEAD);
    },
    isRoleGreaterThanHead() {
      return this.currentRole > USER_LEVEL.HEAD;
    },
    isTableCheckable() {
      const checkboxCol = this.columns?.find(({ column }) => column === CHECKBOX_KEY);
      return checkboxCol?.visible || false;
    },
    maxDate() {
      return dayjs().toDate();
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    searchPlaceholder() {
      const placeholders = {
        [SEARCH_OPTION_KEYS.ACCOUNT]: '請輸入帳號',
        [SEARCH_OPTION_KEYS.EMAIL]: '請輸入Email',
        [SEARCH_OPTION_KEYS.NAME]: '請輸入姓名',
        [SEARCH_OPTION_KEYS.SERIAL]: '請輸入人員編號',
      };
      return placeholders[this.searchBy.key] || '請輸入姓名、帳號、Email、人員編號';
    },
    searchQuery() {
      const search = {};
      if (!this.searchBy.key || this.searchBy.key === SEARCH_OPTION_KEYS.ALL) {
        searchOptions.forEach(({ key }) => {
          search[key] = this.search;
        });
      } else {
        search[this.searchBy.key] = this.search;
      }

      if (this.advancedSearchRoles?.length) {
        search.roles = this.advancedSearchRoles;
      }
      if (this.advancedSearchUnits?.length) {
        search.department_ids = this.advancedSearchUnits.map(({ key }) => (key));
      }
      if (this.advancedSearchDates?.length) {
        const [start, end] = this.advancedSearchDates || [];
        search.created_at = {
          lt: dayjs(end).endOf('day').format('YYYY-MM-DD HH:mm'),
          gt: dayjs(start).startOf('day').format('YYYY-MM-DD HH:mm'),
        };
      }
      return {
        status: 1, // NOTE: Only search with enabled members
        search,
      };
    },
    roleBadge() {
      return {
        [USER_LEVEL.ADMIN]: {
          name: '管理員',
          style: 'admin',
        },
        [USER_LEVEL.OWNER]: {
          name: '帳號擁有者',
          style: 'owner',
        },
        [USER_LEVEL.HEAD]: {
          name: '團主',
          style: 'head',
        },
      };
    },
    total() {
      return this.membersList?.pagination?.total || 0;
    },
    userRoles() {
      return USER_LEVEL_MAPPING;
    },
    userUnits() {
      return this.departments?.filter(({ id }) => id !== NO_DEPARTMENT_ID)
        .map(({ id, name }) => ({ key: id, value: name })) || [];
    },
  },
  methods: {
    ...mapActions('member', {
      getMemberList: GET_MEMBER_LIST,
    }),
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    dateFormatter(arr) {
      return arr.map((time) => this.formatTime(time, 'YYYY/MM/DD')).join(' - ');
    },
    formatTime(time, format = 'YYYY-MM-DD') {
      return dayjs(time).format(format);
    },
    async loadDepartmentList() {
      this.loading = true;
      this.departments = await this.getDepartmentList();
      this.loading = false;
    },
    onCreateMember() {
      this.$router.push('/member/create');
    },
    onBatchCreateMember() {
      this.$router.push('/member/batch-create');
    },
    onClearSearchFilters() {
      this.searchBy = {
        key: SEARCH_OPTION_KEYS.ALL,
        value: SEARCH_OPTION_VALUES.ALL,
      };
      this.search = '';
      this.advancedSearchDates = [];
      this.advancedSearchRoles = [];
      this.advancedSearchUnits = [];
      this.onSearch();
    },
    async onDownloadMembers() {
      this.isPreparingDownload = true;
      try {
        const res = await downloadMemberList(this.searchQuery);
        const downloadMemberRowsDatum = res?.map(({
          account,
          allowOrderOnSite,
          department_name: unit,
          email,
          ext,
          name,
          note,
          phone,
          role,
          serial,
        }, index) => ([
          index + 1,
          serial,
          account,
          this.getUserRole(role),
          name,
          unit,
          email,
          ext,
          phone,
          allowOrderOnSite,
          note,
        ])) || [];
        const rows = [
          ['No.', '人員編號', '帳號', '角色', '姓名', '部門', 'Email', '分機/電話', '手機號碼', '可現場訂單次數', '備註'],
        ].concat(downloadMemberRowsDatum);

        csvDownloader(rows, '人員名單');
      } catch (error) {
        Toast.open({
          type: 'is-danger',
          message: error.message,
        });
      } finally {
        this.isPreparingDownload = false;
      }
    },
    onShowPrintPreview() {
      this.showPreviewPrintQRCodes = true;
    },
    async onSearch() {
      this.checkedRows = [];
      await this.setPageOrGetData();
      this.members = this.allMembers;
    },
    onSelectSearchTag(values, value) {
      const item = values || [];
      const findIndex = item.findIndex((itemKey) => value === itemKey);
      if (findIndex !== -1) {
        item.splice(findIndex, 1);
      } else {
        item.push(value);
      }
    },
    memberRowClass(row) {
      if (!row.verified) {
        return 'is-grey-300';
      }
      if (row.status === MEMBER_STATUS.DISABLED) {
        return 'is-grey-300';
      }
      return 'is-black';
    },
    mapPhoneNumber,
    getDepartmentName(departmentId) {
      const department = this.departments.find((d) => d.id === departmentId);
      return department?.name;
    },
    getUserRole(role) {
      const roleKey = Object.keys(USER_LEVEL_MAPPING)
        .find((key) => USER_LEVEL_MAPPING[key]?.key === role);
      return USER_LEVEL_MAPPING?.[roleKey]?.name || '';
    },
    editMember(member) {
      if (this.isRoleGreaterThanHead) {
        this.$router.push(`/member/${member.id}/edit`);
      }
    },
    async onBatchDeleteUser() {
      try {
        this.deletingMembers = true;
        const memberIds = this.checkedRows?.map(({ id }) => id) || [];
        await batchDeleteMembers(memberIds);
        this.$snackbar({ type: SNACKBAR_TYPES.SUCCESS, title: '刪除成功', msg: '人員已批次成功刪除' });
        this.openBatchDeleteDialog = false;
        await this.getMemberList({ page: FIRST_PAGE, per_page: this.perPage, ...this.searchQuery });
        this.checkedRows = [];
        this.members = this.allMembers;
      } catch (ex) {
        this.$snackbar({ type: SNACKBAR_TYPES.ERROR, title: '刪除失敗', msg: ex.message });
      } finally {
        this.deletingMembers = false;
      }
    },
    setSelectBy(option) {
      this.searchBy = option;
    },
    setPageOrGetData() {
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.getMemberList({ page: this.page, per_page: this.perPage, ...this.searchQuery });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper {
  padding: 0 100px;
  @include apply-to-tablet {
    padding: 0 2rem;
  }
  @include apply-to-phone {
    padding: 0 5px;
  }
}

.member-table {
  padding: 1rem;
  margin-top: 1rem;
  background-color: #fff;
  border-radius: 2px;
  ::v-deep &--editable {
    tbody tr {
      cursor: pointer;
    }
  }
  &__header {
    margin: 16px 0;
    &--title {
      display: inline-block;
      color: $black;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 40px;
      margin-bottom: 1.5rem;
    }
    &--subtitle {
      display: inline-block;
      margin-left: 1rem;
      font-size: .875rem;
      color: $black;
    }
    &--action {
      font-size: .875rem;
      vertical-align: top;
      margin-left: 2rem;
      i:before {
        color: $white;
      }
    }
    &--toggle-column {
      @apply flex flex-wrap gap-4 items-baseline text-right;

      color: $dark-grey;
      ::v-deep .dropdown-trigger {
        font-size: .8rem;
        cursor: pointer;
      }
    }
  }
}

.batch-button {
  @apply flex items-center p-2 rounded;

  border: 1px solid transparent;

  &:disabled {
    border-color: transparent;

    &:hover {
      border-color: transparent;
    }
  }
  &:hover {
    border-color: var(--gray-500);
  }
}

.dropdown {
  &-item {
    color: $black;
    padding-right: 0;
    &:hover {
      background-color: map.get($custom-colors, "light-grey-400");
    }
    label {
      width: 100%;
    }
  }
}

.loading {
  @apply animate-spin
    border-2
    border-t-2
    border-t-transparent
    border-gray-500
    flex
    items-center
    justify-center
    rounded-full;
}

.rounded-button {
  @apply bg-white
    border
    border-gray-700
    hover:bg-gray-700
    hover:border-gray-700
    hover:text-white
    px-2
    py-1
    rounded-full
    text-gray-700
    text-sm;

  &.active {
    @apply bg-gray-700 border-gray-700 text-white;
  }

  &.disabled {
    @apply border-gray-50 text-gray-50;
  }
}

.search-block {
  @apply flex
    items-start
    justify-between
    md:flex-col
    md:items-start
    sm:flex-col
    sm:items-start;
}

::v-deep .b-table {
  box-shadow: 0px 0px 4px 1px $lighter-grey;
  transition: box-shadow 0.2s ease-in-out;
  border-radius: 4px;
  &:hover {
    box-shadow: 0px 0px 10px 1px $lighter-grey;
  }
  .verification {
    &.pending {
      color: $light-blue;
    }
    &.expired {
      color: $red;
    }
  }
  .role {
    &.owner {
      color: map.get($custom-colors, 'badge-3');
    }
    &.admin {
      color: map.get($custom-colors, 'badge-1');
    }
    &.head {
      color: map.get($custom-colors, 'badge-2');
    }
  }

  .table {
    thead th {
      height: 40px;
      line-height: 40px;
      font-size: .75rem;
      font-weight: 500;
      color: $black;
      padding: 0 .75rem;
      border: 1px solid map.get($custom-colors, 'light-grey-500');
      border-width: 0 0 1px;
      &.is-sortable {
        &:hover {
          border-color: map.get($custom-colors, 'grey-300');
        }
        &.is-current-sort {
          color: $green;
          border-color: map.get($custom-colors, 'grey-300');
        }
      }
    }
    tbody tr {
      height: 3rem;
      font-size: .875rem;
      td {
        vertical-align: middle;
      }
      &:not(:last-child) td {
        font-size: .875rem;
        border-bottom: 1px solid map.get($custom-colors, 'light-grey-500');
        transition: border-bottom-color 0.1s;
      }
      & .note-cell {
        max-width: 184px;
        overflow: hidden;
        word-break: break-all;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      & .action-cell:hover {
        i::before {
          color: $red;
        }
      }
      &.is-grey-50 {
        color: map.get($custom-colors, 'grey-50');
      }
      &.is-grey-300 {
        color: map.get($custom-colors, 'grey-300');
      }
      &.is-black {
        color: map.get($custom-colors, 'black');
      }
      &:hover {
        background-color: $white;
        td {
          border-bottom-color: $light-blue;
        }
      }
      .level {
        padding-bottom: 0;
      }
    }
  }
}
</style>
